import { useState } from "react";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { CustomerGroup } from "../../../types/customer.type";
import PrintSetsDialog from "./PrintSetsDialog";
import { useCustomerGroups } from "./useCustomerGroups";

const COLUMNS = ["รหัส", "Prefix", "ชื่อหมวด", "ชุดพิมพ์เริ่มต้น (ชุดที่ 1)", "จัดการ"];
const SKELETON_ROW_COUNT = 3;
const SKELETON_ROWS = Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => index);
const EMPTY_CELL_SX = { py: 5 } as const;
const PAPER_SX = { p: 2 } as const;
const STANDARD_PRINT_SETS_TEXT = "ค่ามาตรฐาน";

interface GroupRowProps {
  group: CustomerGroup;
  onEdit: (group: CustomerGroup) => void;
}

function GroupRow({ group, onEdit }: Readonly<GroupRowProps>) {
  const handleEdit = () => onEdit(group);

  return (
    <TableRow>
      <TableCell>{group.code}</TableCell>
      <TableCell>{group.prefix}</TableCell>
      <TableCell>{group.name}</TableCell>
      <TableCell>
        {group.default_print_sets ? (
          <Stack direction="row" spacing={0.5}>
            {group.default_print_sets.map((copyType) => (
              <Chip key={copyType} size="small" color="primary" variant="outlined" label={copyType} />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {STANDARD_PRINT_SETS_TEXT}
          </Typography>
        )}
      </TableCell>
      <TableCell align="center">
        <Tooltip title="แก้ไขชุดพิมพ์เริ่มต้น">
          <IconButton size="small" color="primary" onClick={handleEdit}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

/**
 * Per customer group, the copies ชุดที่ 1 prints for a CASH customer that has no print setting of
 * its own. A customer's own print setting always wins over its group's.
 */
export default function CustomerGroupPrintSets() {
  const { groups, loading, reload } = useCustomerGroups();
  const [editingGroup, setEditingGroup] = useState<CustomerGroup | null>(null);

  const isEmpty = !loading && groups.length === 0;

  const handleCloseEdit = () => setEditingGroup(null);

  const handleSaved = () => {
    setEditingGroup(null);
    reload();
  };

  return (
    <Paper sx={PAPER_SX}>
      <Stack spacing={0.5} mb={2}>
        <Typography variant="subtitle1" fontWeight={700}>
          ชุดพิมพ์เริ่มต้นตามหมวดลูกค้า
        </Typography>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              SKELETON_ROWS.map((row) => (
                <TableRow key={row}>
                  {COLUMNS.map((column) => (
                    <TableCell key={column}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {isEmpty && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={EMPTY_CELL_SX}>
                  <Typography color="text.secondary">ไม่พบหมวดลูกค้า</Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              groups.map((group) => (
                <GroupRow key={group.uuid} group={group} onEdit={setEditingGroup} />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {editingGroup && (
        <PrintSetsDialog group={editingGroup} onClose={handleCloseEdit} onSaved={handleSaved} />
      )}
    </Paper>
  );
}
